/**
 * Plan Collect&Go basket lines for a Menu product + mapped articles.
 * Mirrors sync selection (priority tiers + cheapestPackCombo).
 */

import { cogoLineTotalCents, cogoUnitPriceCents } from './cogoPrice'
import {
  cartQuantity,
  cheapestPackCombo,
  chooseUnitType,
  menuNeededGrams,
  packForUnitType,
  type UnitType,
} from './cogoUnits'

function mapArticlesByPriority<T extends { priority?: number }>(
  articles: T[]
): T[][] {
  const byPri = new Map<number, T[]>()
  for (const a of articles) {
    const p = a.priority != null && a.priority >= 1 ? a.priority : 1
    const list = byPri.get(p) || []
    list.push(a)
    byPri.set(p, list)
  }
  return [...byPri.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([, list]) => list)
}

export type CogoPlanArticle = {
  technicalProductId: string
  priority?: number
  unitType?: UnitType | null
  display: string
  assortmentUrl?: string | null
  unitLabel?: string | null
  weightConversionFactor?: number | null
  packSize?: number | null
  priceCents?: number | null
  backendUnit?: string | null
  unitTypes?: UnitType[] | null
  weightStepGrams?: number | null
  hasImage?: boolean
}

export type CogoPlanPick = {
  technicalProductId: string
  display: string
  assortmentUrl: string | null
  unitLabel: string | null
  weightConversionFactor: number | null
  packSize: number | null
  priceCents: number | null
  backendUnit: string | null
  unitType: UnitType | null
  hasImage: boolean
  units: number
  quantityUnit: 'st' | 'kg'
  label: string
  lineTotalCents: number | null
  unitPriceCents: number | null
}

/** How much Menu quantity one pick covers (same unit as neededUnit). */
export function pickCoveredMenuQty(
  pick: Pick<
    CogoPlanPick,
    | 'units'
    | 'quantityUnit'
    | 'weightConversionFactor'
    | 'packSize'
    | 'unitType'
  >,
  neededUnit: string
): number | null {
  if (!(pick.units > 0)) return 0
  const u = (neededUnit || '').toLowerCase()
  const wantWeight =
    u.includes('gram') ||
    u === 'g' ||
    u === 'kg' ||
    u.includes('kilogram') ||
    u === 'ml' ||
    u.includes('milliliter') ||
    u.includes('millilitre') ||
    u === 'cl' ||
    u.includes('centiliter') ||
    u.includes('centilitre') ||
    u === 'l' ||
    u === 'liter' ||
    u === 'litre' ||
    u.includes('liter')
  const asBase =
    u === 'kg' ||
    u.includes('kilogram') ||
    u === 'l' ||
    u === 'liter' ||
    u === 'litre' ||
    (u.includes('liter') && !u.includes('milli') && !u.includes('centi'))
  // cl → report in cl (÷10 from ml-equivalent); ml/g stay as stored scale.

  if (wantWeight) {
    let grams: number | null = null
    if (pick.quantityUnit === 'kg') {
      grams = pick.units * 1000
    } else if (
      pick.weightConversionFactor != null &&
      pick.weightConversionFactor > 0
    ) {
      grams = pick.units * pick.weightConversionFactor
    }
    if (grams == null) return null
    if (asBase) return grams / 1000
    if (u === 'cl' || u.includes('centiliter') || u.includes('centilitre')) {
      return grams / 10
    }
    return grams
  }

  // Pieces
  if (pick.packSize != null && pick.packSize > 1) {
    return pick.units * pick.packSize
  }
  return pick.units
}

export function picksCoveredMenuQty(
  picks: CogoPlanPick[],
  neededUnit: string
): number | null {
  let sum = 0
  let any = false
  for (const pick of picks) {
    const c = pickCoveredMenuQty(pick, neededUnit)
    if (c == null) continue
    any = true
    sum += c
  }
  return any ? sum : null
}

export type CoverageStatus = 'under' | 'exact' | 'over'

export function coverageStatus(
  covered: number | null,
  needed: number
): CoverageStatus | null {
  if (covered == null || !(needed > 0)) return null
  const eps = Math.max(1e-6, needed * 1e-9)
  if (covered + eps < needed) return 'under'
  if (covered > needed + eps) return 'over'
  return 'exact'
}

/** Rebuild pick label/total after changing cart units. */
export function withPickUnits(pick: CogoPlanPick, units: number): CogoPlanPick {
  const u = Math.max(0, units)
  const packLabel =
    pick.unitLabel ||
    (pick.weightConversionFactor
      ? `${pick.weightConversionFactor}g`
      : pick.quantityUnit === 'kg'
        ? 'kg'
        : 'st')
  const label =
    pick.quantityUnit === 'kg'
      ? `${String(u).replace('.', ',')} kg`
      : `${u}× ${packLabel}`
  const lineTotalCents = cogoLineTotalCents({
    priceCents: pick.priceCents,
    backendUnit: pick.backendUnit,
    units: u,
    quantityUnit: pick.quantityUnit,
    weightConversionFactor: pick.weightConversionFactor,
  })
  return { ...pick, units: u, label, lineTotalCents }
}

/**
 * Apply per-article cart quantity overrides on top of planned picks.
 * Empty overrides → planned picks unchanged.
 */
export function applyBasketQuantities(
  picks: CogoPlanPick[],
  basketQuantities: Record<string, number> | null | undefined
): CogoPlanPick[] {
  if (!basketQuantities || Object.keys(basketQuantities).length === 0) {
    return picks
  }
  return picks.map((pick) => {
    const raw = basketQuantities[pick.technicalProductId]
    if (raw == null || !Number.isFinite(raw)) return pick
    return withPickUnits(pick, Number(raw))
  })
}

/**
 * Apply stored Cogo cart overrides when they still match recipe need;
 * otherwise fall back to the computed plan (stale sync overrides).
 */
export function resolvePicksWithBasketQuantities(
  planned: CogoPlanPick[],
  basketQuantities: Record<string, number> | null | undefined,
  effectiveQuantity: number,
  neededUnit: string
): CogoPlanPick[] {
  if (!basketQuantities || Object.keys(basketQuantities).length === 0) {
    return planned
  }
  const overridden = applyBasketQuantities(planned, basketQuantities)
  const covPlan = picksCoveredMenuQty(planned, neededUnit)
  const covOver = picksCoveredMenuQty(overridden, neededUnit)
  const dist = (c: number | null) =>
    c == null ? Infinity : Math.abs(c - effectiveQuantity)
  if (dist(covOver) > dist(covPlan) + 1e-6) {
    return planned
  }
  return overridden
}

type TierArticle = CogoPlanArticle & { priority: number }

function tryCartQty(
  art: CogoPlanArticle,
  neededQty: number,
  neededUnit: string,
  unitType: UnitType | null
) {
  const packed = packForUnitType(unitType, art)
  // packForUnitType(P) clears packSize — keep article piece-count (6st/12st box).
  const packSize =
    unitType === 'P' && art.packSize != null && art.packSize > 0
      ? art.packSize
      : packed.packSize
  // packForUnitType(P) clears weightConversionFactor for the stepper; keep
  // article content size so ml/gram needs still convert to pack counts.
  // For W, packed.weightConversionFactor is the kg step (may come from "100g" label).
  const weightConversionFactor =
    packed.weightConversionFactor ?? art.weightConversionFactor ?? null
  return cartQuantity({
    neededQty,
    neededUnit,
    weightConversionFactor,
    packSize,
    unitType,
    // Pass original label so "100g" beats a dual-produce scrape step of 10g.
    weightStepGrams:
      unitType === 'W' ? packed.weightConversionFactor : art.weightStepGrams,
    unitLabel: art.unitLabel || packed.unitLabel,
  })
}

function pickFromArticle(
  art: CogoPlanArticle,
  neededQty: number,
  neededUnit: string
): CogoPlanPick | null {
  const mappedSell = art.unitType ?? null
  let unitType = mappedSell ?? chooseUnitType(neededUnit, art.unitTypes || null)
  let q = tryCartQty(art, neededQty, neededUnit, unitType)
  // Mapped/scraped as W only, but Menu needs pieces → order per st.
  if (!q && unitType === 'W') {
    unitType = 'P'
    q = tryCartQty(art, neededQty, neededUnit, unitType)
  }
  if (!q) return null
  const packed = packForUnitType(unitType, art)
  const packLabel =
    art.unitLabel ||
    packed.unitLabel ||
    (art.weightConversionFactor ? `${art.weightConversionFactor}g` : 'st')
  // Always keep the article's content weight (WCF / bag / 25cl), never the
  // per-kg stepper (10g/100g) — that would turn €2,49/kg into €249/kg in
  // formatCogoChoicePrice when backendUnit is missing.
  const weightConversionFactorForPrice = art.weightConversionFactor ?? null
  return {
    technicalProductId: art.technicalProductId,
    display: art.display,
    assortmentUrl: art.assortmentUrl ?? null,
    unitLabel: art.unitLabel ?? null,
    weightConversionFactor: weightConversionFactorForPrice,
    packSize: art.packSize ?? null,
    priceCents: art.priceCents ?? null,
    backendUnit: art.backendUnit ?? null,
    unitType,
    hasImage: Boolean(art.hasImage),
    units: q.value,
    quantityUnit: q.unit,
    label: q.label || `${q.value}× ${packLabel}`,
    lineTotalCents: cogoLineTotalCents({
      priceCents: art.priceCents,
      backendUnit: art.backendUnit,
      units: q.value,
      quantityUnit: q.unit,
      weightConversionFactor: weightConversionFactorForPrice,
    }),
    unitPriceCents: cogoUnitPriceCents(art),
  }
}

/**
 * Chosen Cogo article lines for a needed Menu quantity.
 * Empty when unmapped or quantity ≤ 0.
 */
export function planCogoPicks(
  articles: CogoPlanArticle[],
  neededQty: number,
  neededUnit: string
): CogoPlanPick[] {
  if (!(neededQty > 0) || articles.length === 0) return []

  const normalized: TierArticle[] = articles.map((a) => ({
    ...a,
    priority: a.priority != null && a.priority >= 1 ? a.priority : 1,
  }))

  if (normalized.length === 1) {
    const one = pickFromArticle(normalized[0]!, neededQty, neededUnit)
    return one ? [one] : []
  }

  const needG = menuNeededGrams(neededQty, neededUnit)
  const tiers = mapArticlesByPriority(normalized)

  if (needG != null) {
    for (const tier of tiers) {
      const combo = cheapestPackCombo(tier, needG)
      if (combo && combo.length > 0) {
        return combo.map((pick) => {
          const art = pick.article
          const packLabel =
            art.unitLabel ||
            (art.weightConversionFactor
              ? `${art.weightConversionFactor}g`
              : 'pak')
          const units = pick.count
          return {
            technicalProductId: art.technicalProductId,
            display: art.display,
            assortmentUrl: art.assortmentUrl ?? null,
            unitLabel: art.unitLabel ?? null,
            weightConversionFactor: art.weightConversionFactor ?? null,
            packSize: art.packSize ?? null,
            priceCents: art.priceCents ?? null,
            backendUnit: art.backendUnit ?? null,
            unitType: art.unitType ?? null,
            hasImage: Boolean(art.hasImage),
            units,
            quantityUnit: 'st' as const,
            label: `${units}× ${packLabel}`,
            lineTotalCents: cogoLineTotalCents({
              priceCents: art.priceCents,
              backendUnit: art.backendUnit,
              units,
              quantityUnit: 'st',
              weightConversionFactor: art.weightConversionFactor,
            }),
            unitPriceCents: cogoUnitPriceCents(art),
          }
        })
      }
    }
  }

  // Fallback: best single article from first tier
  const first = tiers[0]?.[0]
  if (!first) return []
  const one = pickFromArticle(first, neededQty, neededUnit)
  return one ? [one] : []
}
